import React from 'react';
import Heading from "@react/react-spectrum/Heading";
import Datepicker from '@react/react-spectrum/Datepicker';
import Button from '@react/react-spectrum/Button';
import Dialog from "@react/react-spectrum/Dialog";
import Select from "@react/react-spectrum/Select";
import { FlexDiscount } from "../../../models/FlexDiscount";
import { normalizeTimeToHms, setPromoEndDate } from "../../../providers/FlexDiscountsAPI";
import { getCurrentUserDistributorInfo } from "../../../utils/Functions/utils";
import './ViewEditAvailableFlexDiscountsPage.css';

/** Time slice from ISO datetime for <input type="time"> (must be HH:mm:ss, no Z/offset). */
function parseTimeFromIsoDate(iso?: string | null): string {
    if (!iso || !iso.includes('T')) return '00:00:00';
    let timePart = iso.split('T')[1] ?? '';
    timePart = timePart.replace(/Z$/i, '');
    timePart = timePart.replace(/[+-]\d{2}:\d{2}(:\d{2})?$/, '');
    timePart = timePart.replace(/[+-]\d{4}$/, '');
    timePart = timePart.split('.')[0];
    if (/^\d{2}:\d{2}:\d{2}$/.test(timePart)) return timePart;
    if (/^\d{2}:\d{2}$/.test(timePart)) return `${timePart}:00`;
    return '00:00:00';
}

function EditReusableFlexDiscounts({ reusableDiscounts }: { reusableDiscounts: FlexDiscount[] }) {
    const [selectedPromoId, setSelectedPromoId] = React.useState('');
    const [selectedEndDate, setSelectedEndDate] = React.useState('');
    const [selectedEndTime, setSelectedEndTime] = React.useState('00:00:00');
    const [saving, setSaving] = React.useState(false);
    const [dialogState, setDialogState] = React.useState<{ open: boolean; title: string; message: string }>({
        open: false,
        title: '',
        message: ''
    });

    const uniquePromosById = React.useMemo(() => {
        const byId = new Map<string, FlexDiscount>();
        for (const fd of reusableDiscounts) {
            if (!byId.has(fd.id)) {
                byId.set(fd.id, fd);
            }
        }
        return Array.from(byId.values());
    }, [reusableDiscounts]);

    const promoOptions = React.useMemo(
        () =>
            uniquePromosById.map((fd) => ({
                label: `${fd.name} (${fd.code})`,
                value: fd.id
            })),
        [uniquePromosById]
    );

    const selectedPromo = React.useMemo(
        () => (selectedPromoId ? uniquePromosById.find((fd) => fd.id === selectedPromoId) : null),
        [selectedPromoId, uniquePromosById]
    );

    const dateRange = React.useMemo(() => {
        if (!selectedPromo) return { min: undefined, max: undefined };

        const toDateOnly = (s?: string | null) => {
            if (!s) return undefined;
            return s.includes('T') ? s.split('T')[0] : s;
        };

        return {
            min: toDateOnly(selectedPromo.startDate),
            max: toDateOnly(selectedPromo.discountLockEndDate ?? selectedPromo.reusableUntil)
        };
    }, [selectedPromo]);

    React.useEffect(() => {
        if (selectedPromo) {
            const raw = selectedPromo.endDate ?? '';
            setSelectedEndDate(raw.includes('T') ? raw.split('T')[0] : raw);
            setSelectedEndTime(parseTimeFromIsoDate(raw));
        } else {
            setSelectedEndDate('');
            setSelectedEndTime('00:00:00');
        }
    }, [selectedPromo]);

    const handleSave = async () => {
        if (!selectedPromoId) {
            setDialogState({
                open: true,
                title: "Missing selection",
                message: "Please select a flex discount."
            });
            return;
        }
        if (!selectedEndDate) {
            setDialogState({
                open: true,
                title: "Missing end date",
                message: "Please select an end date before saving."
            });
            return;
        }
        if (!selectedEndTime?.trim()) {
            setDialogState({
                open: true,
                title: "Missing end time",
                message: "Please select an end time before saving."
            });
            return;
        }

        const userInfo = getCurrentUserDistributorInfo();
        const partnerId = userInfo?.selectedDistributor?.imsOrgId;
        if (!partnerId) {
            setDialogState({
                open: true,
                title: "Missing distributor",
                message: "Could not determine the selected distributor to update this promotion."
            });
            return;
        }

        setSaving(true);
        try {
            const response = await setPromoEndDate(
                selectedPromoId,
                selectedEndDate,
                partnerId,
                normalizeTimeToHms(selectedEndTime.trim())
            );

            if (response.httpstatus >= 200 && response.httpstatus < 300) {
                setDialogState({
                    open: true,
                    title: "Saved",
                    message: "End date updated successfully."
                });
            } else {
                setDialogState({
                    open: true,
                    title: "Save failed",
                    message: response.message || `Request failed (HTTP ${response.httpstatus}).`
                });
            }
        } catch (e: any) {
            setDialogState({
                open: true,
                title: "Save failed",
                message: e?.message || "An unexpected error occurred."
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Heading variant="subtitle3">Select a flex discount</Heading>
            <Select
                className="edit_reusable_until_select"
                aria-label="Select flex discount"
                value={selectedPromoId}
                options={promoOptions}
                placeholder="Select a flex discount"
                onChange={(value) => setSelectedPromoId(Array.isArray(value) ? value[0] ?? '' : String(value ?? ''))}
            />
            <br />
            <br />

            <Heading variant="subtitle3">Select an end date</Heading>
            <Datepicker
                value={selectedEndDate}
                min={dateRange.min}
                max={dateRange.max}
                onChange={(value) => {
                    if (typeof value === 'string') {
                        setSelectedEndDate(value);
                    }
                }}
                selectionType="single"
                type="date"
            />
            <br />
            <br />
            <br />

            <Heading variant="subtitle3">Select end time</Heading>
            <div className="edit-reusable-flex-time-picker-wrapper">
                <input
                    type="time"
                    step={1}
                    value={normalizeTimeToHms(selectedEndTime)}
                    onChange={(e) => setSelectedEndTime(normalizeTimeToHms(e.target.value))}
                    className="edit-reusable-flex-time-native"
                    aria-label="End time"
                />
            </div>

            <br /><br /><br />

            <Button variant="action" disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save'}
            </Button>

            <Dialog
                title={dialogState.title}
                open={dialogState.open}
                confirmLabel="OK"
                onConfirm={() => setDialogState((prev) => ({ ...prev, open: false }))}
            >
                {dialogState.message}
            </Dialog>
        </div>
    );
}

export default EditReusableFlexDiscounts;
